import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  Filter,
  Calendar,
  MapPin,
  User,
  Loader2,
  ChevronRight,
  EyeOff,
  Eye,
} from 'lucide-react';
import { formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';

const BookingsPage = () => {
  const { api, isManager } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active'); // Default to active (hides completed)
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Handle the 'active' filter which excludes completed and cancelled
      if (statusFilter === 'active') {
        // Don't pass status - we'll filter client-side
      } else if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await api().get(`/bookings?${params.toString()}`);
      let fetchedBookings = response.data.bookings;
      
      // For 'active' filter, exclude completed and cancelled on client side
      if (statusFilter === 'active') {
        fetchedBookings = fetchedBookings.filter(b => 
          !['completed', 'cancelled'].includes(b.status)
        );
      }
      
      setBookings(fetchedBookings);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      booking.client_name?.toLowerCase().includes(search) ||
      booking.service_address?.toLowerCase().includes(search) ||
      booking.assigned_staff_name?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight">Bookings</h1>
          <p className="text-zinc-500 mt-1">Manage all cleaning jobs</p>
        </div>
        {isManager && (
          <Link to="/bookings/new">
            <Button className="bg-zinc-900 hover:bg-zinc-800" data-testid="new-booking-btn">
              <Plus className="mr-2 h-4 w-4" />
              New Booking
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <Card className="border-zinc-100">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                placeholder="Search bookings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-bookings-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">
                  <span className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Active Jobs
                  </span>
                </SelectItem>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">
                  <span className="flex items-center gap-2">
                    <EyeOff className="h-4 w-4" />
                    Completed
                  </span>
                </SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        </div>
      ) : filteredBookings.length === 0 ? (
        <Card className="border-zinc-100">
          <CardContent className="py-12 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
              <Calendar className="h-8 w-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-medium text-zinc-900 mb-1">No bookings found</h3>
            <p className="text-zinc-500">
              {searchTerm ? 'Try adjusting your search' : 'Create your first booking to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredBookings.map((booking) => (
            <Card
              key={booking.id}
              className="border-zinc-100 hover:border-zinc-200 transition-colors cursor-pointer"
              onClick={() => navigate(`/bookings/${booking.id}`)}
              data-testid={`booking-row-${booking.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getStatusColor(booking.status)}>
                        {getStatusLabel(booking.status)}
                      </Badge>
                      <span className="text-sm text-zinc-500">
                        {formatDate(booking.scheduled_date)}
                      </span>
                      {booking.time_window_start && (
                        <span className="text-sm text-zinc-400">
                          {booking.time_window_start}
                        </span>
                      )}
                    </div>
                    <h3 className="font-medium text-zinc-900 truncate">{booking.client_name}</h3>
                    <div className="flex items-center gap-4 mt-1 text-sm text-zinc-500">
                      <span className="flex items-center gap-1 truncate">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{booking.service_address}</span>
                      </span>
                      {booking.assigned_staff_name && (
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {booking.assigned_staff_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-zinc-400 flex-shrink-0 ml-4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookingsPage;
